import { useState, useEffect } from 'react'
import { Loader2, Search, Play, ChevronDown, ChevronRight, AlertTriangle, CheckCircle2, XCircle, HelpCircle, FileText, ArrowRight, Shield, Database, User } from 'lucide-react'
import { matchRateApi, countryGuidesApi } from '@/api/client'
import { COUNTRIES } from '../data/countries'
import { showToast } from '@/store/uiStore'
import type { PocRecord } from '../types'

// ─── Types ──────────────────────────────────────────────────────────────

interface QualifyCountryInput {
  country_code: string
  country_name: string
  current_rate: number | null
  target_rate: number | null
  ruleset: '1+1' | '2+2'
  consent: boolean
  address_required: boolean
  prefer_credit_header: boolean
}

interface BulkResultSource {
  name: string
  vendor: string
  coverage: number
  mandatory_pii: string[]
  notes: string
  deal_sheet: string
  missing_deal_sheet: boolean
  pass_rate?: string | null
  pass_rate_checks?: string | null
}

interface BulkResultRow {
  country_code: string
  country_name: string
  ruleset: string
  achievable: boolean
  strength_label: string
  coverage_pct: number
  recommended_sources: BulkResultSource[]
  best_pass_rate?: string | null
  best_pass_rate_source?: string | null
  best_pass_rate_checks?: string | null
  production_data_source?: string | null
  overlap_warning: boolean
  consent_warning: boolean
  warnings: string[]
  alt_ruleset?: string
  alt_achievable?: boolean
  alt_strength_label?: string
  alt_coverage_pct?: number
  alt_sources?: { name: string; vendor: string; coverage: number; mandatory_pii: string[] }[]
  expert_override?: {
    sources: { name: string; vendor: string; coverage: number; reason: string }[]
    strength: number
    summary: string
    warnings: string[]
    required_pii: string[]
  } | null
}

interface QualifyResult {
  input: QualifyCountryInput
  bulk_result: BulkResultRow | null
  considerations_text: string | null
  country_guide: string | null
  generated_at: string
}

interface QualifyData {
  countries: QualifyResult[]
}

// ─── Helpers ────────────────────────────────────────────────────────────

const STRENGTH_COLORS: Record<string, { text: string; bg: string }> = {
  strong: { text: 'text-green-400', bg: 'bg-green-500/10' },
  decent: { text: 'text-cyan-400', bg: 'bg-cyan-500/10' },
  weak: { text: 'text-amber-400', bg: 'bg-amber-500/10' },
  not_achievable: { text: 'text-red-400', bg: 'bg-red-500/10' },
}

function getDefaultInput(): QualifyCountryInput {
  return {
    country_code: '',
    country_name: '',
    current_rate: null,
    target_rate: null,
    ruleset: '1+1',
    consent: false,
    address_required: false,
    prefer_credit_header: false,
  }
}

// ─── Component ──────────────────────────────────────────────────────────

export function PocQualify({ poc, save }: { poc: PocRecord; save: (data: Partial<PocRecord>) => void }) {
  const qualifyData: QualifyData = (poc.discovery_json as any)?.qualification || { countries: [] }
  const [input, setInput] = useState<QualifyCountryInput>(getDefaultInput())
  const [countrySearch, setCountrySearch] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const [running, setRunning] = useState(false)
  const [runningStep, setRunningStep] = useState('')
  const [expandedCountry, setExpandedCountry] = useState<string | null>(null)

  // Pre-fill country from POC's countries_in_scope if only one
  useEffect(() => {
    if (poc.countries_in_scope?.length === 1 && !input.country_code) {
      const code = poc.countries_in_scope[0]
      const country = COUNTRIES.find(c => c.code === code)
      if (country) {
        setInput(prev => ({ ...prev, country_code: code, country_name: country.name }))
        setCountrySearch(country.name)
      }
    }
  }, [poc.countries_in_scope])

  const filteredCountries = countrySearch.length > 0
    ? COUNTRIES.filter(c =>
        c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
        c.code.toLowerCase() === countrySearch.toLowerCase()
      ).slice(0, 15)
    : []

  const selectCountry = (code: string, name: string) => {
    setInput(prev => ({ ...prev, country_code: code, country_name: name }))
    setCountrySearch(name)
    setShowDropdown(false)
  }

  const existingResult = qualifyData.countries.find(c => c.input.country_code === input.country_code)

  const runQualification = async () => {
    if (!input.country_code) {
      showToast('Select a country first', 'error')
      return
    }
    setRunning(true)

    try {
      // Step 1: Run bulk assessment
      setRunningStep('Running match rate assessment...')
      const bulkRes = await matchRateApi.bulk({
        country_codes: [input.country_code],
        ruleset: input.ruleset,
        sort_priority: 'production',
        address_required: input.address_required,
        prefer_credit_header: input.prefer_credit_header,
      })
      console.log('[Qualify] Raw bulk response:', bulkRes)
      if (!bulkRes.success) {
        console.error('[Qualify] Bulk assessment failed:', bulkRes.error)
        showToast(`Assessment error: ${bulkRes.error || 'Unknown error'}`, 'error')
      }
      const bulkRows = (bulkRes as any)?.data || []
      const bulkResult: BulkResultRow | null = bulkRows[0] || null
      console.log('[Qualify] Bulk result for', input.country_code, bulkResult)

      if (!bulkRes.success) {
        setRunning(false)
        setRunningStep('')
        return
      }

      // Step 2: Fetch country guide — generate one if it doesn't exist yet
      setRunningStep('Fetching country guide...')
      let countryGuide: string | null = null
      try {
        const guideRes = await countryGuidesApi.get(input.country_code)
        console.log('[Qualify] Country guide response:', guideRes.success, guideRes.data?.status, guideRes.data?.generated_content?.length ?? 0)
        if (guideRes.success && guideRes.data?.generated_content) {
          countryGuide = guideRes.data.generated_content
        } else {
          // Guide doesn't exist or has no content — create & generate it
          setRunningStep('Generating country verification guide...')
          if (!guideRes.success) {
            await countryGuidesApi.create(input.country_code, input.country_name)
          }
          const genRes = await countryGuidesApi.generate(input.country_code)
          if (genRes.success && genRes.data?.content) {
            countryGuide = genRes.data.content
          }
        }
      } catch (e) {
        console.error('Country guide fetch/generate failed (non-blocking):', e)
      }
      console.log('[Qualify] Final countryGuide length:', countryGuide?.length ?? 0)

      // Step 3: Store result
      setRunningStep('Saving results...')
      const newResult: QualifyResult = {
        input: { ...input },
        bulk_result: bulkResult,
        considerations_text: null,
        country_guide: countryGuide,
        generated_at: new Date().toISOString(),
      }

      const updatedCountries = qualifyData.countries.filter(c => c.input.country_code !== input.country_code)
      updatedCountries.push(newResult)

      const updatedDiscovery = {
        ...poc.discovery_json,
        qualification: { countries: updatedCountries },
      }
      save({ discovery_json: updatedDiscovery as any })
      setExpandedCountry(input.country_code)
      showToast(`Qualification complete for ${input.country_name}`, 'success')
    } catch (e) {
      console.error('Qualification failed:', e)
      showToast('Qualification failed — check console', 'error')
    } finally {
      setRunning(false)
      setRunningStep('')
    }
  }

  const removeCountryResult = (code: string) => {
    const updatedCountries = qualifyData.countries.filter(c => c.input.country_code !== code)
    const updatedDiscovery = {
      ...poc.discovery_json,
      qualification: { countries: updatedCountries },
    }
    save({ discovery_json: updatedDiscovery as any })
  }

  return (
    <div className="space-y-6 py-4">
      {/* Header */}
      <div>
        <h3 className="text-base font-semibold text-[var(--text-primary)]">Country Qualification</h3>
        <p className="text-xs text-[var(--text-muted)] mt-1">
          Assess verification coverage and data requirements per country. Results can be shared with customers to set expectations on pass rates and required data inputs.
        </p>
      </div>

      {/* Input Form */}
      <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4 space-y-4">
        {/* Country selector */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="relative">
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Country</label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
              <input
                type="text"
                className="w-full h-10 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg pl-9 pr-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-cyan-500"
                placeholder="Search countries..."
                value={countrySearch}
                onChange={e => { setCountrySearch(e.target.value); setShowDropdown(true) }}
                onFocus={() => setShowDropdown(true)}
                onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
              />
            </div>
            {showDropdown && filteredCountries.length > 0 && (
              <div className="absolute z-20 top-full mt-1 left-0 right-0 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {filteredCountries.map(c => (
                  <button
                    key={c.code}
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-colors flex items-center justify-between"
                    onMouseDown={() => selectCountry(c.code, c.name)}
                  >
                    <span>{c.name}</span>
                    <span className="text-[10px] text-[var(--text-muted)] font-mono">{c.code}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Ruleset</label>
            <div className="flex gap-2">
              {(['1+1', '2+2'] as const).map(r => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setInput(prev => ({ ...prev, ruleset: r }))}
                  className={`flex-1 h-10 text-sm font-medium rounded-lg border transition-colors ${
                    input.ruleset === r
                      ? 'border-cyan-500 bg-cyan-500/10 text-[var(--text-primary)]'
                      : 'border-[var(--border-color)] text-[var(--text-muted)] hover:border-gray-500'
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Pass rates */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
              Current Pass Rate <span className="text-[var(--text-muted)]">(%)</span>
            </label>
            <input
              type="number"
              min={0}
              max={100}
              className="w-full h-10 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-cyan-500"
              placeholder="e.g. 65"
              value={input.current_rate ?? ''}
              onChange={e => setInput(prev => ({ ...prev, current_rate: e.target.value === '' ? null : Number(e.target.value) }))}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
              Target Pass Rate <span className="text-[var(--text-muted)]">(%)</span>
            </label>
            <input
              type="number"
              min={0}
              max={100}
              className="w-full h-10 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-cyan-500"
              placeholder="e.g. 85"
              value={input.target_rate ?? ''}
              onChange={e => setInput(prev => ({ ...prev, target_rate: e.target.value === '' ? null : Number(e.target.value) }))}
            />
          </div>
        </div>

        {/* Options row */}
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="accent-cyan-500"
              checked={input.consent}
              onChange={e => setInput(prev => ({ ...prev, consent: e.target.checked }))}
            />
            <span className="text-xs text-[var(--text-secondary)]">Has consent</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="accent-cyan-500"
              checked={input.address_required}
              onChange={e => setInput(prev => ({ ...prev, address_required: e.target.checked }))}
            />
            <span className="text-xs text-[var(--text-secondary)]">Address required</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="accent-cyan-500"
              checked={input.prefer_credit_header}
              onChange={e => setInput(prev => ({ ...prev, prefer_credit_header: e.target.checked }))}
            />
            <span className="text-xs text-[var(--text-secondary)]">Prefer credit header</span>
          </label>
        </div>

        {/* Run button */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={runQualification}
            disabled={running || !input.country_code}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg bg-cyan-600 text-white hover:bg-cyan-500 transition-colors disabled:opacity-50"
          >
            {running ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
            {running ? runningStep : existingResult ? 'Re-run Qualification' : 'Run Qualification'}
          </button>
          {input.country_code && (
            <span className="text-xs text-[var(--text-muted)]">
              {input.country_name} ({input.country_code})
              {existingResult && ' — has existing results'}
            </span>
          )}
        </div>
      </div>

      {/* Results */}
      {qualifyData.countries.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-[var(--text-secondary)]">
            Qualification Results ({qualifyData.countries.length} {qualifyData.countries.length === 1 ? 'country' : 'countries'})
          </h4>

          {qualifyData.countries.map(result => (
            <CountryResultCard
              key={result.input.country_code}
              result={result}
              expanded={expandedCountry === result.input.country_code}
              onToggle={() => setExpandedCountry(prev => prev === result.input.country_code ? null : result.input.country_code)}
              onRemove={() => removeCountryResult(result.input.country_code)}
              onReload={() => {
                setInput(result.input)
                setCountrySearch(result.input.country_name)
              }}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Country Result Card ────────────────────────────────────────────────

function CountryResultCard({
  result,
  expanded,
  onToggle,
  onRemove,
  onReload,
}: {
  result: QualifyResult
  expanded: boolean
  onToggle: () => void
  onRemove: () => void
  onReload: () => void
}) {
  const { input, bulk_result: bulk } = result
  const strength = bulk?.strength_label || 'not_achievable'
  const colors = STRENGTH_COLORS[strength] || STRENGTH_COLORS.not_achievable

  const allPii = new Set<string>()
  if (bulk?.recommended_sources) {
    bulk.recommended_sources.forEach(s => s.mandatory_pii?.forEach(p => allPii.add(p)))
  }
  if (bulk?.expert_override?.required_pii) {
    bulk.expert_override.required_pii.forEach(p => allPii.add(p))
  }

  return (
    <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg overflow-hidden">
      {/* Header row */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[var(--bg-tertiary)] transition-colors"
      >
        {expanded ? <ChevronDown size={14} className="text-[var(--text-muted)] shrink-0" /> : <ChevronRight size={14} className="text-[var(--text-muted)] shrink-0" />}

        <span className="text-sm font-medium text-[var(--text-primary)] flex-1">
          {input.country_name}
          <span className="ml-2 text-[10px] font-mono text-[var(--text-muted)]">{input.country_code}</span>
        </span>

        {/* Strength badge */}
        <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${colors.text} ${colors.bg}`}>
          {strength === 'not_achievable' ? 'Not Achievable' : strength.charAt(0).toUpperCase() + strength.slice(1)}
        </span>

        {/* Ruleset */}
        <span className="text-[10px] font-mono text-[var(--text-muted)] bg-[var(--bg-tertiary)] px-2 py-0.5 rounded">
          {input.ruleset}
        </span>

        {/* Pass rates summary */}
        {(input.current_rate != null || input.target_rate != null) && (
          <span className="text-[10px] text-[var(--text-muted)]">
            {input.current_rate != null && `${input.current_rate}%`}
            {input.current_rate != null && input.target_rate != null && ' → '}
            {input.target_rate != null && <span className="text-cyan-400">{input.target_rate}%</span>}
          </span>
        )}

        {/* Coverage */}
        {bulk && bulk.coverage_pct > 0 && (
          <span className="text-[10px] text-[var(--text-muted)]">
            {Math.round(bulk.coverage_pct)}% coverage
          </span>
        )}
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-[var(--border-color)] px-4 py-4 space-y-5">
          {/* Assessment Summary */}
          {bulk ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                {bulk.achievable ? (
                  <CheckCircle2 size={16} className="text-green-400" />
                ) : (
                  <XCircle size={16} className="text-red-400" />
                )}
                <h5 className="text-sm font-medium text-[var(--text-primary)]">
                  {input.ruleset} {bulk.achievable ? 'Achievable' : 'Not Achievable'}
                </h5>
              </div>

              {/* Pass rate comparison */}
              {(input.current_rate != null || input.target_rate != null || bulk.best_pass_rate) && (
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-[var(--bg-primary)] rounded-lg p-3 text-center">
                    <div className="text-[10px] text-[var(--text-muted)] mb-1">Current</div>
                    <div className="text-lg font-semibold text-[var(--text-secondary)]">
                      {input.current_rate != null ? `${input.current_rate}%` : '—'}
                    </div>
                  </div>
                  <div className="bg-[var(--bg-primary)] rounded-lg p-3 text-center">
                    <div className="text-[10px] text-[var(--text-muted)] mb-1">FrankieOne Est.</div>
                    <div className={`text-lg font-semibold ${
                      bulk.best_pass_rate ? 'text-cyan-400' : colors.text
                    }`}>
                      {bulk.best_pass_rate || `${Math.round(bulk.coverage_pct)}%`}
                    </div>
                    {bulk.best_pass_rate_source && (
                      <div className="text-[9px] text-[var(--text-muted)] mt-0.5">
                        {bulk.production_data_source === 'benchmark' ? 'Benchmark' : 'Production'} · {bulk.best_pass_rate_source}
                      </div>
                    )}
                  </div>
                  <div className="bg-[var(--bg-primary)] rounded-lg p-3 text-center">
                    <div className="text-[10px] text-[var(--text-muted)] mb-1">Target</div>
                    <div className="text-lg font-semibold text-cyan-400">
                      {input.target_rate != null ? `${input.target_rate}%` : '—'}
                    </div>
                  </div>
                </div>
              )}

              {/* Expert Override */}
              {bulk.expert_override && (
                <div className="border border-purple-500/30 bg-purple-500/5 rounded-lg p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 size={14} className="text-purple-400" />
                    <span className="text-xs font-medium text-purple-400">Expert-Curated Recommendation</span>
                  </div>
                  {bulk.expert_override.summary && (
                    <p className="text-xs text-[var(--text-secondary)]">{bulk.expert_override.summary}</p>
                  )}
                  <div className="flex flex-wrap gap-1.5">
                    {bulk.expert_override.sources.map((s, i) => (
                      <span key={i} className="inline-flex items-center gap-1 px-2 py-1 text-[10px] rounded bg-purple-500/10 text-purple-300 border border-purple-500/20">
                        {s.name} <span className="text-purple-400/60">({s.vendor})</span> · {Math.round(s.coverage)}%
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Verification Flow Diagram */}
              <VerificationFlowDiagram bulk={bulk} input={input} />

              {/* Recommended Sources */}
              {bulk.recommended_sources?.length > 0 && (
                <div>
                  <h6 className="text-xs font-medium text-[var(--text-secondary)] mb-2">Recommended Sources</h6>
                  <div className="space-y-2">
                    {bulk.recommended_sources.map((src, i) => (
                      <div key={i} className="flex items-start gap-3 bg-[var(--bg-primary)] rounded-lg p-3">
                        <span className="text-[10px] font-bold text-cyan-400 bg-cyan-500/10 w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                          {i + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-[var(--text-primary)]">{src.name}</span>
                            <span className="text-[10px] text-[var(--text-muted)]">{src.vendor}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                              src.coverage >= 80 ? 'text-green-400 bg-green-500/10' :
                              src.coverage >= 50 ? 'text-amber-400 bg-amber-500/10' :
                              'text-red-400 bg-red-500/10'
                            }`}>
                              {Math.round(src.coverage)}%
                            </span>
                            {src.pass_rate && (
                              <span className="text-[10px] text-cyan-400 bg-cyan-500/10 px-1.5 py-0.5 rounded font-medium">
                                Prod: {src.pass_rate}
                              </span>
                            )}
                          </div>
                          {src.mandatory_pii?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {src.mandatory_pii.map(pii => (
                                <span key={pii} className="text-[9px] px-1.5 py-0.5 rounded bg-[var(--bg-secondary)] text-[var(--text-muted)] border border-[var(--border-color)]">
                                  {pii}
                                </span>
                              ))}
                            </div>
                          )}
                          {src.notes && (
                            <p className="text-[10px] text-[var(--text-muted)] mt-1">{src.notes}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Required Data Inputs (customer-facing) */}
              {allPii.size > 0 && (
                <div>
                  <h6 className="text-xs font-medium text-[var(--text-secondary)] mb-2">Required Data Inputs from Customer</h6>
                  <div className="flex flex-wrap gap-1.5">
                    {Array.from(allPii).sort().map(pii => (
                      <span key={pii} className="inline-flex items-center px-2.5 py-1 text-xs rounded-lg bg-cyan-500/10 text-cyan-300 border border-cyan-500/20">
                        {pii}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Warnings */}
              {(bulk.warnings?.length > 0 || bulk.overlap_warning || bulk.consent_warning) && (
                <div className="space-y-1.5">
                  {bulk.overlap_warning && (
                    <div className="flex items-start gap-2 text-xs text-amber-400">
                      <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                      <span>Vendor overlap — recommended sources may be from the same vendor</span>
                    </div>
                  )}
                  {bulk.consent_warning && (
                    <div className="flex items-start gap-2 text-xs text-amber-400">
                      <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                      <span>No consent-free path available — customer consent is required</span>
                    </div>
                  )}
                  {[...new Set(bulk.warnings || [])].map((w, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-amber-400">
                      <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                      <span>{w}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Alternative ruleset */}
              {bulk.alt_ruleset && bulk.alt_achievable !== undefined && (
                <div className="bg-[var(--bg-primary)] rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <HelpCircle size={14} className="text-[var(--text-muted)]" />
                    <span className="text-xs text-[var(--text-secondary)]">
                      Alternative: <strong>{bulk.alt_ruleset}</strong> —{' '}
                      {bulk.alt_achievable ? (
                        <span className="text-green-400">{bulk.alt_strength_label} ({Math.round(bulk.alt_coverage_pct || 0)}%)</span>
                      ) : (
                        <span className="text-red-400">Not achievable</span>
                      )}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-xs text-[var(--text-muted)] py-4 text-center">
              No assessment data returned. This may indicate the local database is missing coverage data for this country.
              Try running against production or check the Match Rate Enquiry tool directly.
            </div>
          )}

          {/* Country Guide */}
          {result.country_guide && (
            <CountryGuideSection content={result.country_guide} />
          )}

          {/* Metadata + actions */}
          <div className="flex items-center justify-between border-t border-[var(--border-color)] pt-3">
            <span className="text-[10px] text-[var(--text-muted)]">
              Generated {new Date(result.generated_at).toLocaleString()}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onReload}
                className="text-[10px] text-[var(--text-muted)] hover:text-cyan-400 transition-colors"
              >
                Re-run
              </button>
              <button
                type="button"
                onClick={onRemove}
                className="text-[10px] text-[var(--text-muted)] hover:text-red-400 transition-colors"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Country Guide Section ─────────────────────────────────────────────

function CountryGuideSection({ content }: { content: string }) {
  const [expanded, setExpanded] = useState(false)

  // Show a preview (first ~600 chars) when collapsed
  const isLong = content.length > 600
  const preview = isLong ? content.slice(0, 600) + '...' : content

  return (
    <div className="border-t border-[var(--border-color)] pt-4">
      <button
        type="button"
        onClick={() => setExpanded(prev => !prev)}
        className="flex items-center gap-2 mb-2 hover:opacity-80 transition-opacity"
      >
        <FileText size={14} className="text-cyan-400" />
        <h6 className="text-xs font-medium text-[var(--text-secondary)]">Country Verification Guide</h6>
        {isLong && (
          expanded
            ? <ChevronDown size={12} className="text-[var(--text-muted)]" />
            : <ChevronRight size={12} className="text-[var(--text-muted)]" />
        )}
      </button>
      <div
        className="text-xs text-[var(--text-secondary)] prose prose-invert prose-xs max-w-none [&_h1]:text-sm [&_h2]:text-xs [&_h3]:text-xs [&_p]:text-xs [&_li]:text-xs [&_table]:text-[10px] whitespace-pre-wrap"
      >
        {expanded || !isLong ? content : preview}
      </div>
      {isLong && !expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="text-[10px] text-cyan-400 hover:text-cyan-300 mt-2 transition-colors"
        >
          Show full guide
        </button>
      )}
    </div>
  )
}

// ─── Verification Flow Diagram ─────────────────────────────────────────

function VerificationFlowDiagram({ bulk, input }: { bulk: BulkResultRow; input: QualifyCountryInput }) {
  // Collect all required PII across sources
  const allPii = new Set<string>()
  if (bulk.recommended_sources) {
    bulk.recommended_sources.forEach(s => s.mandatory_pii?.forEach(p => allPii.add(p)))
  }
  const piiList = Array.from(allPii).sort()

  // What gets verified (output fields) — derive from source capabilities
  const verifiedFields: string[] = []
  if (piiList.some(p => p.toLowerCase().includes('name'))) verifiedFields.push('Full Name')
  if (piiList.some(p => p.toLowerCase().includes('dob') || p.toLowerCase().includes('date of birth'))) verifiedFields.push('Date of Birth')
  if (piiList.some(p => p.toLowerCase().includes('national id') || p.toLowerCase().includes('id number'))) verifiedFields.push('National ID')
  if (piiList.some(p => p.toLowerCase().includes('address'))) verifiedFields.push('Address')
  if (piiList.some(p => p.toLowerCase().includes('phone'))) verifiedFields.push('Phone Number')
  if (piiList.some(p => p.toLowerCase().includes('passport'))) verifiedFields.push('Passport')

  const strength = bulk.strength_label || 'not_achievable'
  const colors = STRENGTH_COLORS[strength] || STRENGTH_COLORS.not_achievable

  return (
    <div className="bg-[var(--bg-primary)] rounded-lg p-4 border border-[var(--border-color)]">
      <h6 className="text-xs font-medium text-[var(--text-secondary)] mb-4">Verification Flow</h6>

      <div className="flex items-stretch gap-0 min-h-[160px]">
        {/* LEFT: Customer Data Inputs */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-2.5">
            <User size={12} className="text-blue-400" />
            <span className="text-[10px] font-semibold text-blue-400 uppercase tracking-wider">Customer Sends</span>
          </div>
          <div className="space-y-1">
            {piiList.map(pii => (
              <div key={pii} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-blue-500/8 border border-blue-500/20">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
                <span className="text-[11px] text-blue-300 truncate">{pii}</span>
              </div>
            ))}
            {input.consent && (
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-green-500/8 border border-green-500/20">
                <div className="w-1.5 h-1.5 rounded-full bg-green-400 shrink-0" />
                <span className="text-[11px] text-green-300">consent</span>
              </div>
            )}
          </div>
        </div>

        {/* ARROW */}
        <div className="flex flex-col items-center justify-center px-3 shrink-0">
          <ArrowRight size={18} className="text-[var(--text-muted)]" />
        </div>

        {/* MIDDLE: FrankieOne + Data Sources */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-2.5">
            <Database size={12} className="text-cyan-400" />
            <span className="text-[10px] font-semibold text-cyan-400 uppercase tracking-wider">FrankieOne Routes To</span>
          </div>
          <div className="space-y-1.5">
            {bulk.recommended_sources?.map((src, i) => (
              <div key={i} className="px-2.5 py-2 rounded-md bg-cyan-500/8 border border-cyan-500/20">
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] font-bold text-cyan-400 bg-cyan-500/15 w-4 h-4 rounded-full flex items-center justify-center shrink-0">
                    {i + 1}
                  </span>
                  <span className="text-[11px] font-medium text-cyan-300 truncate">{src.name}</span>
                </div>
                <div className="flex items-center gap-2 mt-1 pl-5.5">
                  <span className="text-[9px] text-[var(--text-muted)]">{src.vendor}</span>
                  <span className={`text-[9px] font-medium ${
                    src.coverage >= 80 ? 'text-green-400' : src.coverage >= 50 ? 'text-amber-400' : 'text-red-400'
                  }`}>{Math.round(src.coverage)}%</span>
                </div>
              </div>
            ))}
            {/* Ruleset badge */}
            <div className="flex items-center gap-2 px-2.5 py-1.5 mt-1">
              <span className="text-[9px] text-[var(--text-muted)]">Ruleset:</span>
              <span className="text-[10px] font-mono font-medium text-cyan-400 bg-cyan-500/10 px-1.5 py-0.5 rounded">{input.ruleset}</span>
            </div>
          </div>
        </div>

        {/* ARROW */}
        <div className="flex flex-col items-center justify-center px-3 shrink-0">
          <ArrowRight size={18} className="text-[var(--text-muted)]" />
        </div>

        {/* RIGHT: Verification Output */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-2.5">
            <Shield size={12} className={colors.text} />
            <span className={`text-[10px] font-semibold uppercase tracking-wider ${colors.text}`}>Verified Output</span>
          </div>
          <div className="space-y-1">
            {verifiedFields.map(field => (
              <div key={field} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md ${colors.bg} border ${
                strength === 'strong' ? 'border-green-500/20' :
                strength === 'decent' ? 'border-cyan-500/20' :
                strength === 'weak' ? 'border-amber-500/20' : 'border-red-500/20'
              }`}>
                <CheckCircle2 size={10} className={`shrink-0 ${colors.text}`} />
                <span className={`text-[11px] ${colors.text}`}>{field}</span>
              </div>
            ))}
          </div>

          {/* Result badge */}
          <div className={`mt-2.5 px-2.5 py-2 rounded-md ${colors.bg} border ${
            strength === 'strong' ? 'border-green-500/30' :
            strength === 'decent' ? 'border-cyan-500/30' :
            strength === 'weak' ? 'border-amber-500/30' : 'border-red-500/30'
          }`}>
            <div className={`text-[10px] font-semibold ${colors.text}`}>
              {bulk.achievable ? 'PASS' : 'FAIL'} — {strength.toUpperCase()}
            </div>
            {bulk.best_pass_rate != null && Number(bulk.best_pass_rate) > 0 && (
              <div className="text-[9px] text-[var(--text-muted)] mt-0.5">
                Est. {bulk.best_pass_rate}% based on {bulk.production_data_source === 'benchmark' ? 'benchmark' : 'production'} data
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Consent notice */}
      {bulk.consent_warning && (
        <div className="flex items-center gap-1.5 mt-3 px-2.5 py-1.5 rounded-md bg-amber-500/8 border border-amber-500/20">
          <AlertTriangle size={10} className="text-amber-400 shrink-0" />
          <span className="text-[10px] text-amber-300">Consent required — no consent-free verification path available for this country</span>
        </div>
      )}
    </div>
  )
}
